import React, { useMemo } from 'react';
import { WidgetDataGuard } from '../components/WidgetDataGuard';
import type { WidgetProps } from '../types';
import { useChartData } from '../hooks/useChartData';
import { useDashboardTranslation } from '../i18n';
import { colorsForCategories, MAX_CATEGORIES } from '../utils/chartColors';
import { groupTail, isOthersRow } from '../utils/groupTail';
import { formatCompact, formatExact, resolveCurrency } from '../utils/formatNumber';
import { readFormat } from '../utils/widgetConfig';

/**
 * Barras horizontales — el caballo de batalla del tablero.
 *
 * <b>Por qué es el único que no usa recharts.</b> El diseño pide la etiqueta y el
 * valor en la MISMA LÍNEA, encima de la barra, sobre una pista. Eso no es un
 * `BarChart layout="vertical"`: recharts pone las etiquetas en un eje Y lateral que
 * se come entre el 30 % y el 40 % del ancho y trunca cualquier nombre largo —
 * "Repuestos y accesorios" acaba en "Repuestos y…".
 *
 * Con la etiqueta arriba, la barra dispone de todo el ancho de la tarjeta y ninguna
 * categoría se trunca. Son cuarenta líneas de DOM contra un motor de gráficos
 * entero, y encima se lee mejor.
 *
 * <b>Es el default para distribución por categoría</b>, en reemplazo de la dona:
 * comparar longitudes es más preciso que comparar ángulos, y escala a ocho
 * categorías donde un anillo ya no.
 */
const ChartBarHorizontal: React.FC<WidgetProps> = ({ definition, period, preference }) => {
  const { t } = useDashboardTranslation();
  const { chartData, valueKeys, categoryField, data, loading, error } =
    useChartData(definition, preference, period);

  // `readFormat` valida contra la unión: un `format` que no existe cae a `undefined` en vez de
  // colarse como una cadena cualquiera hasta el formateo.
  const currency = readFormat(definition.config_schema) === 'currency' ? resolveCurrency().symbol : null;

  const valueKey = valueKeys[0];

  const { rows, total, max } = useMemo(() => {
    if (!valueKey || chartData.length === 0) return { rows: [], total: 0, max: 0 };

    // Orden descendente y cola agrupada: un ranking desordenado no es un ranking.
    const grouped = groupTail(chartData, categoryField, valueKey, MAX_CATEGORIES);
    const toNum = (row: Record<string, unknown>) => Number(row[valueKey]) || 0;

    return {
      rows: grouped,
      total: grouped.reduce((acc, row) => acc + toNum(row), 0),
      max: grouped.reduce((acc, row) => Math.max(acc, toNum(row)), 0),
    };
  }, [chartData, categoryField, valueKey]);

  /** Los colores de las barras, resueltos una vez: sin colisiones dentro del gráfico. */
  const colores = useMemo(
    () => colorsForCategories(rows.map(row => String(row[categoryField] ?? ''))),
    [rows, categoryField],
  );

  return (
    <WidgetDataGuard loading={loading} error={error} data={data} shape="bars">
      <div className="w-full h-full overflow-y-auto px-4 pb-4 flex flex-col gap-3">
        {rows.map((row, index) => {
          const rawCategory = String(row[categoryField] ?? '');
          const isOthers = isOthersRow(rawCategory);
          const value = Number(row[valueKey]) || 0;
          const label = isOthers ? t('dashboard.chart.others') : rawCategory;

          // El porcentaje va sobre el TOTAL (cola incluida): es la cifra que el
          // usuario espera al leer "62 %" en una distribución.
          const share = total > 0 ? (value / total) * 100 : 0;
          // El ancho, en cambio, es relativo al MÁXIMO. Con anchos proporcionales al
          // total, un reparto plano dejaría todas las barras diminutas.
          const width = max > 0 ? (value / max) * 100 : 0;

          const exact = formatExact(value, { currency });

          return (
            <div key={`${rawCategory}-${index}`} className="flex flex-col gap-1" data-testid="hbar-row">
              <div className="flex items-baseline justify-between gap-3 text-[12px]">
                <span className="truncate" style={{ color: 'var(--dash-text-primary)' }} title={label}>
                  {label}
                  {isOthers && typeof row.__groupedCount === 'number' && (
                    <span style={{ color: 'var(--dash-text-muted)' }}>{` (${row.__groupedCount})`}</span>
                  )}
                </span>
                <span
                  className="flex-shrink-0"
                  style={{ color: 'var(--dash-text-secondary)', fontVariantNumeric: 'tabular-nums' }}
                  title={exact}
                >
                  {formatCompact(value, { currency })}
                  <span style={{ color: 'var(--dash-text-muted)' }}>{` · ${share.toFixed(0)} %`}</span>
                </span>
              </div>

              {/* Pista + barra. La pista es lo que da la referencia de cuánto falta. */}
              <div
                className="w-full rounded-full overflow-hidden"
                style={{ height: 6, background: 'var(--dash-surface-page)' }}
                role="img"
                aria-label={`${label}: ${exact} (${share.toFixed(0)} %)`}
              >
                <div
                  className="h-full rounded-full transition-[width] duration-300"
                  style={{ width: `${width}%`, background: colores[rawCategory] }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </WidgetDataGuard>
  );
};

export default ChartBarHorizontal;
